// Modal de détail d'un message système
import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  getMessageIcon,
  getMessageColor,
  getMessageBgClass,
  getMessageTypeLabel,
  getRelativeTime,
} from '../utils/messageHelpers';
import { useMessage } from '../contexts/MessageContext';
import { X } from 'lucide-react';

/**
 * @param {{ message: Object|null, open: boolean, onClose: Function }} props
 */
const MessageDetailModal = ({ message, open, onClose }) => {
  const { markAsRead } = useMessage();
  const navigate = useNavigate();

  // Marquer comme lu à l'ouverture
  useEffect(() => {
    if (open && message?.id && !message.isRead) {
      markAsRead(message.id);
    }
  }, [open, message?.id, message?.isRead, markAsRead]);

  if (!message) return null;

  const color = getMessageColor(message.type);

  const handleActionClick = () => {
    if (!message.actionButton?.link) return;
    const link = message.actionButton.link;
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
        {/* En-tête avec type et date */}
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
              style={{ backgroundColor: `${color}25` }}
            >
              {getMessageIcon(message.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getMessageBgClass(message.type)}`}
                >
                  {getMessageTypeLabel(message.type)}
                </span>
                {message.priority === 'high' && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                    Prioritaire
                  </span>
                )}
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {getRelativeTime(message.publishedAt)}
                </span>
              </div>
            </div>
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
            {message.title}
          </DialogTitle>
        </DialogHeader>

        {/* Image optionnelle */}
        {message.imageUrl && (
          <div className="my-4 rounded-xl overflow-hidden">
            <img
              src={message.imageUrl}
              alt={message.title}
              className="w-full object-cover max-h-64"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Contenu HTML du message */}
        <div
          className="prose dark:prose-invert prose-sm max-w-none text-gray-700 dark:text-gray-300"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.content, {
            ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
          }) }}
          style={{
            lineHeight: '1.7',
          }}
        />

        {/* Bouton d'action */}
        {message.actionButton?.label && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleActionClick}
              className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg"
              style={{ backgroundColor: color }}
            >
              {message.actionButton.label} →
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageDetailModal;
